import { useShopI18n } from '../../i18n'
import type { CheckoutSavedAddress } from '../addressBookAdapter'
import type { CheckoutAddressForm } from '../flow'
import { CheckoutAddressPanel } from './CheckoutAddressPanel'
import { SavedAddressCards } from './SavedAddressCards'
import { useCheckoutAddressPanel } from './useCheckoutAddressPanel'

type Props = {
  form: CheckoutAddressForm
  validationByField: Map<string, string>
  sameAsShipping: boolean
  shippingSummary: string
  savedAddresses: CheckoutSavedAddress[]
  disabled: boolean
  onFieldChange: (field: keyof CheckoutAddressForm, value: string) => void
  onSetForm: (value: CheckoutAddressForm) => void
  onSameAsShippingChange: (value: boolean) => void
  onSubmit: () => void
  onBack: () => void
}

export const BillingAddressStep = ({
  form,
  validationByField,
  sameAsShipping,
  shippingSummary,
  savedAddresses,
  disabled,
  onFieldChange,
  onSetForm,
  onSameAsShippingChange,
  onSubmit,
  onBack,
}: Props) => {
  const { t } = useShopI18n()
  const addressPanel = useCheckoutAddressPanel({
    customAddress: {
      defaultBilling: false,
      defaultShipping: false,
      idPrefix: 'billing-custom',
      label: 'Billing',
      useBilling: true,
      useShipping: false,
    },
    enabled: !sameAsShipping,
    form,
    missingFieldsMessage: t('checkout.addressBook.panelMissingFields'),
    onSetForm,
    savedAddresses,
    selectSavedAddress: (entry) => entry.useBilling || entry.defaultBilling,
  })

  return (
    <section className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">{t('checkout.billingAddress.title')}</h2>
        <p className="mt-1 text-sm text-slate-600">{t('checkout.billingAddress.description')}</p>
      </header>

      <fieldset className="space-y-2 rounded-lg border border-slate-200 p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('checkout.billing.title')}</legend>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="radio"
            checked={sameAsShipping}
            disabled={disabled}
            onChange={() => onSameAsShippingChange(true)}
          />
          <span>{t('checkout.billing.useShippingAddress')}</span>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="radio"
            checked={!sameAsShipping}
            disabled={disabled}
            onChange={() => onSameAsShippingChange(false)}
          />
          <span>{t('checkout.billing.useDifferentAddress')}</span>
        </label>
      </fieldset>

      {sameAsShipping ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('checkout.billing.previewTitle')}</p>
          <p className="mt-1">{shippingSummary}</p>
        </div>
      ) : (
        <>
          <SavedAddressCards
            groupLabel={t('checkout.billingAddress.title')}
            addresses={addressPanel.addresses}
            selectedAddressId={addressPanel.selectedAddressId}
            disabled={disabled}
            onSelectAddress={addressPanel.selectAddress}
            onEditAddress={addressPanel.editAddress}
            onAddNewAddress={addressPanel.addNewAddress}
          />

          {addressPanel.isFormPanelOpen ? (
            <CheckoutAddressPanel
              disabled={disabled}
              editingAddressId={addressPanel.editingAddressId}
              form={form}
              formIdPrefix="checkout-billing"
              onCancel={addressPanel.cancelPanelAddress}
              onFieldChange={onFieldChange}
              onSubmit={addressPanel.submitPanelAddress}
              panelError={addressPanel.panelError}
              validationByField={validationByField}
            />
          ) : null}
        </>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          onClick={onBack}
        >
          {t('checkout.cta.back')}
        </button>
        <button
          type="button"
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={onSubmit}
        >
          {t('checkout.cta.continue')}
        </button>
      </div>
    </section>
  )
}
